import type { Axios } from "axios";
import { function as function_, taskEither } from "fp-ts";

import { OrderEntity, OrderServicePorts } from "../../model";

import { URLS } from "./consts";
import {
  ChangeStatus,
  Common,
  Create,
  Delete,
  Get,
  GetMany,
  GetPaginated,
  SpecifyPayment,
  Update,
  UpdateServices,
} from "./ios";
import { NUMBER_BY_BUILDING_TYPE, NUMBER_BY_GENDER, NUMBER_BY_REFERRAL_SOURCE } from "./ios/common";
import { mapDtoToEntity, mapDtoToEntity2, mapServices } from "./mappers";

import { Request, Response } from "~/shared/api";
import { AuthenticationError, NotFoundError, UnexpectedError } from "~/shared/error";
import { Pagination } from "~/shared/zod";

class ApiProvider extends OrderServicePorts.Provider {
  private readonly axios: Axios;

  constructor(axios: Axios) {
    super();
    this.axios = axios;
  }

  override create({
    from,
    to,
    cargoDescription,
    referralInfo,
    contactPerson,
    moveIsScheduledFor,
    services,
  }: OrderServicePorts.ProviderIos.Create.In): taskEither.TaskEither<
    UnexpectedError,
    OrderServicePorts.ProviderIos.Common.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(URLS.create, {
            fromCity: from.city,
            fromAddress: from.address,
            fromFloor: from.floor,
            fromPostalCode: from.postalCode,
            fromBuildingType: NUMBER_BY_BUILDING_TYPE[from.buildingType],
            fromBuildingArea: from.floorArea,
            fromRoomCount: from.numberOfRooms,
            fromHasLift: from.hasElevator,
            fromHasAttic: from.hasAttic,
            fromHasBasement: from.hasBasement,
            toCity: to.city,
            toAddress: to.address,
            toFloor: to.floor,
            toPostalCode: to.postalCode,
            toBuildingType: NUMBER_BY_BUILDING_TYPE[to.buildingType],
            toBuildingArea: to.floorArea,
            toRoomCount: to.numberOfRooms,
            toHasLift: to.hasElevator,
            toHasAttic: to.hasAttic,
            toHasBasement: to.hasBasement,
            whatToTransport: cargoDescription,
            infoSource: NUMBER_BY_REFERRAL_SOURCE[referralInfo.source],
            ...(referralInfo.source === OrderEntity.ReferralSourceSchema.OTHER && {
              infoSourceDetails: referralInfo.details,
            }),
            contactName: contactPerson.fullName,
            contactEmail: contactPerson.email,
            contactPhone: contactPerson.phone,
            contactGender: NUMBER_BY_GENDER[contactPerson.gender],
            services: services.map(({ id, ...rest }) => ({
              ...rest,
              serviceId: id,
            })),
            orderDateTime: moveIsScheduledFor,
          }),
        Create.zRes,
      ),
      taskEither.flatMap(({ body }) => {
        return taskEither.right(mapDtoToEntity(body.data));
      }),
    );
  }

  override update({
    from,
    to,
    cargoDescription,
    referralInfo,
    contactPerson,
    moveIsScheduledFor,
    ...rest
  }: OrderServicePorts.ProviderIos.Update.In): taskEither.TaskEither<
    | UnexpectedError
    | AuthenticationError
    | NotFoundError
    | OrderServicePorts.ProviderIos.Common.UpdateError,
    OrderServicePorts.ProviderIos.Common.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.put(URLS.update, {
            ...rest,
            fromCity: from?.city,
            fromAddress: from?.address,
            fromFloor: from?.floor,
            fromPostalCode: from?.postalCode,
            ...(from?.buildingType && {
              fromBuildingType: NUMBER_BY_BUILDING_TYPE[from.buildingType],
            }),
            fromBuildingArea: from?.floorArea,
            fromRoomCount: from?.numberOfRooms,
            fromHasLift: from?.hasElevator,
            fromHasAttic: from?.hasAttic,
            fromHasBasement: from?.hasBasement,
            toCity: to?.city,
            toAddress: to?.address,
            toFloor: to?.floor,
            toPostalCode: to?.postalCode,
            ...(to?.buildingType && {
              toBuildingType: NUMBER_BY_BUILDING_TYPE[to.buildingType],
            }),
            toBuildingArea: to?.floorArea,
            toRoomCount: to?.numberOfRooms,
            toHasLift: to?.hasElevator,
            toHasAttic: to?.hasAttic,
            toHasBasement: to?.hasBasement,
            whatToTransport: cargoDescription,
            ...(referralInfo?.source && {
              infoSource: NUMBER_BY_REFERRAL_SOURCE[referralInfo.source],
              ...(referralInfo.source === OrderEntity.ReferralSourceSchema.OTHER && {
                infoSourceDetails: referralInfo.details,
              }),
            }),
            contactName: contactPerson?.fullName,
            contactEmail: contactPerson?.email,
            contactPhone: contactPerson?.phone,
            ...(contactPerson?.gender && {
              contactGender: NUMBER_BY_GENDER[contactPerson.gender],
            }),
            orderDateTime: moveIsScheduledFor,
          }),
        Update.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 400) {
          const { codeErrors } = response.body.errorDetails;
          if (codeErrors.includes("HTTP_NOT_FOUND")) return taskEither.left(new NotFoundError());

          if (codeErrors.includes("ORDER_COMPLETED"))
            return taskEither.left(
              new OrderServicePorts.ProviderIos.Common.UpdateError(
                OrderServicePorts.ProviderIos.Common.UpdateErrorReason.ORDER_COMPLETED,
              ),
            );
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override updateServices({
    id,
    services,
  }: OrderServicePorts.ProviderIos.UpdateServices.In): taskEither.TaskEither<
    | UnexpectedError
    | AuthenticationError
    | NotFoundError
    | OrderServicePorts.ProviderIos.Common.UpdateError,
    OrderServicePorts.ProviderIos.UpdateServices.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.put(URLS.updateServices, {
            orderId: id,
            services: services.map(({ id, ...rest }) => ({
              ...rest,
              serviceId: id,
            })),
          }),
        UpdateServices.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200)
          return taskEither.right({
            id: body.data.id,
            services: mapServices(body.data.services),
          });

        if (status === 400) {
          const { codeErrors } = response.body.errorDetails;
          if (codeErrors.includes("HTTP_NOT_FOUND")) return taskEither.left(new NotFoundError());

          if (codeErrors.includes("ORDER_COMPLETED"))
            return taskEither.left(
              new OrderServicePorts.ProviderIos.Common.UpdateError(
                OrderServicePorts.ProviderIos.Common.UpdateErrorReason.ORDER_COMPLETED,
              ),
            );
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override delete({
    id,
  }: OrderServicePorts.ProviderIos.Delete.In): taskEither.TaskEither<
    UnexpectedError | NotFoundError,
    void
  > {
    return function_.pipe(
      Response.process(() => this.axios.delete(URLS.delete(id)), Delete.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 204) return taskEither.right(void 0);

        if (status === 404 && response.body.errorDetails.codeErrors.includes("HTTP_NOT_FOUND"))
          return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override specifyPayment({
    id,
    payment,
  }: OrderServicePorts.ProviderIos.SpecifyPayment.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    void
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.put(URLS.specifyPayment, {
            orderId: id,
            paid: payment.isCompleted,
            totalCost: payment.isCompleted ? payment.amount : 0,
          } satisfies Pick<Common.OutSchema, "totalCost"> & {
            orderId: Common.OutSchema["id"];
            paid: Common.OutSchema["isPaid"];
          }),
        SpecifyPayment.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(void 0);

        if (status === 400 && response.body.errorDetails.codeErrors.includes("HTTP_NOT_FOUND"))
          return taskEither.left(new NotFoundError());

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override get({
    id,
  }: OrderServicePorts.ProviderIos.Get.In): taskEither.TaskEither<
    UnexpectedError | NotFoundError,
    OrderServicePorts.ProviderIos.Common.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.get(id)), Get.zRes),
      taskEither.flatMap(({ status, body }) => {
        if (status === 200) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new NotFoundError());
      }),
    );
  }

  override getMany({
    pageOptions,
  }: OrderServicePorts.ProviderIos.Common.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    OrderServicePorts.ProviderIos.GetMany.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.getMany), GetMany.zRes),
      taskEither.flatMap(({ status, body }) => {
        if (status === 200) {
          const { data } = body;
          const {
            success,
            data: pageMeta,
            error,
          } = Pagination.Meta.zSchema.safeParse({
            options: pageOptions,
            totalItems: data.length,
          });

          if (!success) return taskEither.left(new UnexpectedError(error));

          return taskEither.right({
            items: data
              .slice(pageMeta.itemsSkipped, pageMeta.itemsSkipped + pageMeta.size)
              .map(mapDtoToEntity2),
            meta: pageMeta,
          } satisfies OrderServicePorts.ProviderIos.GetMany.Out);
        }

        return taskEither.left(new AuthenticationError());
      }),
    );
  }

  override changeStatus({
    id,
    status,
  }: OrderServicePorts.ProviderIos.ChangeStatus.In): taskEither.TaskEither<
    UnexpectedError | NotFoundError,
    void
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.put(URLS.changeStatus, {
            orderId: id,
            status: Common.NUMBER_BY_STATUS[status],
          }),
        ChangeStatus.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(void 0);

        if (status === 400 && response.body.errorDetails.codeErrors.includes("HTTP_NOT_FOUND"))
          return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getPaginated(
    params: OrderServicePorts.ProviderIos.GetPaginated.In,
  ): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    OrderServicePorts.ProviderIos.GetPaginated.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(
            URLS.getPaginated,
            Request.ListParams.mapListParams(params, GetPaginated.MAP_OF_FIELDS_TO_SORT_BY),
          ),
        GetPaginated.zRes,
      ),
      taskEither.flatMap(({ body }) => {
        const {
          data: { items, totalCount },
        } = body;
        const {
          success,
          data: pageMeta,
          error,
        } = Pagination.Meta.zSchema.safeParse({
          options: params.pagination,
          totalItems: totalCount,
        });

        if (!success) return taskEither.left(new UnexpectedError(error));

        return taskEither.right({
          items: items.map(mapDtoToEntity2),
          meta: pageMeta,
        } satisfies OrderServicePorts.ProviderIos.GetPaginated.Out);
      }),
    );
  }
}

export { ApiProvider };
